import {Alert, Button, Input, Modal} from 'antd';
import React, {useState} from 'react';
import {CredentialResponse, GoogleLogin, GoogleOAuthProvider, GsiButtonConfiguration} from '@react-oauth/google';
import {useLoading} from '../../../../../utils/hooks';
import {Loader} from '../Loader';
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import {trackRegistration} from '../../utils/functions/trackRegistration';

enum GoogleSignInAction {
  Auth = 'auth',
  Register = 'register',
}

enum GoogleSignInNext {
  Dashboard = 'dashboard',
  PromptPassword = 'prompt-password',
  Onboard = 'onboard',
}

interface GoogleSignInError {
  loc?: string;
  msg: string;
  type?: string;
}

interface GoogleSignInResponse {
  action: GoogleSignInAction;
  next: GoogleSignInNext;
  email: string;
  error?: string;
}

export interface GoogleSignInRequest {
  google_user_id_token: string;
  attributed?: boolean;
  password?: string;
}

interface GoogleSignInProps {
  googleSignIn: (requestData: GoogleSignInRequest) => Promise<GoogleSignInResponse>;
  analyticsService: any;
  signText?: GsiButtonConfiguration['text'];
}

const PasswordSchema = Yup.object().shape({
  password: Yup.string().required('Please enter your password'),
});

const ErrorAlert = ({error}: {error: string | null}) => {
  if (!error) return null;
  return <Alert message="" description={error} type="error" style={{marginTop: '10px', marginBottom: '10px'}} />;
};

const ERROR_MESSAGES = {
  'Invalid issuer': 'Neither Google nor us recognize the issuer of token',
  'Invalid token': 'Invalid Google sign-in token',
  'Email provided': 'Email is missing from Google account details',
  'Not allowed': 'Request not from Growth-x',
};

const getErrorMessage = (error: any): string => {
  if (!error.response?.data?.error) {
    return 'An error occurred during Google Sign In';
  }

  const {error: errorData} = error.response.data;

  if (Array.isArray(errorData)) {
    return errorData.map((err: GoogleSignInError) => err.msg).join(', ');
  }

  return ERROR_MESSAGES[errorData] ?? errorData;
};

const GOOGLE_SIGNIN_ID = process.env.NX_PUBLIC_GOOGLE_SIGNIN_ID ?? '';

export const GoogleSignIn: React.FC<GoogleSignInProps> = ({
  googleSignIn,
  analyticsService,
  signText = 'signin_with',
}) => {
  const {loading, startLoading, stopLoading} = useLoading(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [storedCredential, setStoredCredential] = useState<string>('');

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse, formPassword?: string) => {
    try {
      setGoogleError(null);
      startLoading();

      if (credentialResponse.credential) {
        // resend request with the same credentials when password required
        setStoredCredential(credentialResponse.credential);
      }

      const requestData: GoogleSignInRequest = {
        google_user_id_token: credentialResponse.credential,
        ...(formPassword && {password: formPassword}),
      };

      const response: GoogleSignInResponse = await googleSignIn(requestData);
      const {action, next, email} = response;

      if (action === GoogleSignInAction.Auth) {
        if (next === GoogleSignInNext.Dashboard) {
          window.location.href = '/';
        } else if (next === GoogleSignInNext.PromptPassword) {
          setShowPasswordPrompt(true);
        }
      } else if (action === GoogleSignInAction.Register) {
        trackRegistration({
          analyticsService,
          email,
        });

        window.location.href = '/';
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setGoogleError(getErrorMessage(error));
    } finally {
      stopLoading();
    }
  };

  const handlePasswordSubmit = async (values: {password: string}, {resetForm}: FormikHelpers<{password: string}>) => {
    await handleGoogleSuccess({credential: storedCredential}, values.password);

    resetForm();
  };

  const handlePasswordModalClose = () => {
    setShowPasswordPrompt(false);
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_SIGNIN_ID}>
      <div className="googleSigninContainer">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setGoogleError('Google Sign In Failed')}
          text={signText}
          useOneTap
        />
        {loading && <Loader />}
        <ErrorAlert error={googleError} />
        <Modal title="Enter Password" open={showPasswordPrompt} onCancel={handlePasswordModalClose} footer={null}>
          <Formik initialValues={{password: ''}} validationSchema={PasswordSchema} onSubmit={handlePasswordSubmit}>
            {({isSubmitting}) => (
              <Form>
                <div>
                  <Field as={Input.Password} name="password" placeholder="Enter your password" />
                  <ErrorMessage name="password" component="div" className="error-message" />
                </div>
                <ErrorAlert error={googleError} />
                <div style={{marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
                  <Button onClick={handlePasswordModalClose}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={loading} disabled={isSubmitting}>
                    Submit
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      </div>
    </GoogleOAuthProvider>
  );
};
